import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowRight, ArrowsClockwise, CheckCircle, Warning } from '@phosphor-icons/react';

const FieldRow = ({ label, value }) => (
  <div className="border border-[#1F2937] bg-[#0A0C10] p-3">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] terminal-font mb-1">{label}</div>
    <div className="text-sm body-font text-[#F3F4F6] leading-relaxed">{value}</div>
  </div>
);

const FieldList = ({ label, items, emptyLabel = '—' }) => (
  <div className="border border-[#1F2937] bg-[#0A0C10] p-3">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] terminal-font mb-2">{label}</div>
    {items && items.length > 0 ? (
      <ul className="space-y-1">
        {items.map((item, idx) => (
          <li key={idx} className="text-sm terminal-font text-[#F3F4F6]">• {item}</li>
        ))}
      </ul>
    ) : (
      <div className="text-sm terminal-font text-[#9CA3AF]">{emptyLabel}</div>
    )}
  </div>
);

const HandoffPackageModal = ({
  open,
  onOpenChange,
  session,
  pendingReplacementId,
  candidateAI,
  handoffPackage,
  onConfirm,
  onCancel,
  mode = 'replacement'
}) => {
  const [reviewed, setReviewed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const isConfirmingRef = useRef(false);

  const currentProduction = session?.production_ai;
  const project = session?.project;

  const fromAiName = handoffPackage?.from_ai || currentProduction?.name || 'Not connected';
  const toAiName = handoffPackage?.to_ai || (mode === 'replacement' ? candidateAI?.name : currentProduction?.name) || 'Next Demo Production AI';

  const objective = handoffPackage?.project_objective || project?.description || 'No objective set';
  const currentPhase = handoffPackage?.current_phase || project?.current_phase || 'unknown';
  const artifacts = handoffPackage?.artifacts || project?.files_created || [];
  const files = [...(project?.files_created || []), ...(project?.files_modified || [])];
  const constraints = handoffPackage?.constraints || [
    'Preserve approved UI structure',
    'Do not modify approved components',
    'Continue in Demo Mode'
  ];
  const pendingTasks = handoffPackage?.pending_tasks || [
    'Continue work in the next assigned phase',
    'Validate previous approved artifacts',
    'Report progress to Origin AI'
  ];
  const knownErrors = handoffPackage?.known_errors || [];
  const nextExpectedResult = mode === 'replacement' 
    ? 'A verified artifact for the next project phase, produced by the candidate Production AI after human confirmation.'
    : 'A verified artifact for the next project phase, produced by Production AI after human confirmation.';

  const handleConfirm = async () => {
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;
    setConfirming(true);
    try {
      await onConfirm(pendingReplacementId);
    } finally {
      isConfirmingRef.current = false;
      setConfirming(false);
      setReviewed(false);
    }
  };

  const handleCloseOrCancel = async () => {
    setReviewed(false);
    if (onCancel) {
      await onCancel();
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) handleCloseOrCancel(); }}>
      <DialogContent
        data-testid="handoff-package-modal"
        className="max-w-3xl bg-[#13131A] border border-[#1F2937] text-[#F3F4F6] p-0 sm:rounded-none"
      >
        <DialogHeader className="p-6 pb-4 border-b border-[#1F2937]">
          <DialogTitle className="text-xl font-bold terminal-font text-[#F3F4F6] tracking-tight flex items-center gap-3">
            <ArrowsClockwise size={20} className="text-[#D97706]" weight="bold" />
            {mode === 'replacement' ? 'Handoff Package (Vista Previa de Propuesta)' : 'Handoff Package (Transferencia Funcional)'}
          </DialogTitle>
          <DialogDescription className="text-xs terminal-font text-[#9CA3AF] uppercase tracking-[0.2em] mt-1">
            Revisión Humana Requerida · {mode === 'replacement' ? 'El reemplazo NO se aplicará sin tu confirmación' : 'El paquete no será transferido a la IA de Producción sin tu aprobación explícita'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] px-6">
          <div className="space-y-3 py-2">
            {/* Warning Banner */}
            <div className="flex items-start gap-2 p-3 bg-[#0A0C10] border border-[#D97706] rounded-sm">
              <Warning size={18} className="text-[#D97706] mt-0.5 shrink-0" weight="fill" />
              <div className="text-xs terminal-font text-[#D97706] leading-relaxed">
                <strong>⚠️ PREVIEW / ACCIÓN REQUERIDA:</strong> {mode === 'replacement' ? 'El reemplazo aún NO se ha aplicado a la sesión. La IA de Producción actual sigue activa hasta que presiones "Confirm Replacement".' : 'El handoff aún NO ha comenzado. El paquete está pendiente de aprobación.'}
              </div>
            </div>

            {/* Transfer Path Header */}
            <div className="flex items-center gap-3 p-3 bg-[#0A0C10] border border-[#1F2937]">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] terminal-font">IA Actual (From)</div>
                <div className="text-sm terminal-font text-[#F3F4F6] font-bold">{fromAiName}</div>
              </div>
              <ArrowRight size={20} className="text-[#00E5FF]" />
              <div className="flex-1 text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] terminal-font">{mode === 'replacement' ? 'IA Candidata (To)' : 'IA Destino (To)'}</div>
                <div className="text-sm terminal-font text-[#00E5FF] font-bold">{toAiName}</div>
              </div>
            </div>

            {mode === 'handoff' && (
              <>
                <FieldRow label="Handoff ID" value={handoffPackage?.id || handoffPackage?.handoff_id || 'Unknown'} />
                <FieldRow label="Package Hash" value={<span className="break-all">{handoffPackage?.package_hash || 'Unknown'}</span>} />
                <FieldRow label="Instruction" value={handoffPackage?.instruction || 'None'} />
              </>
            )}

            {mode === 'replacement' && (
              <>
                <FieldRow label="IA de Producción Actual" value={fromAiName} />
                <FieldRow label="IA de Producción Candidata" value={toAiName} />
              </>
            )}
            
            <FieldRow label="Fase Asignada" value={currentPhase} />
            <FieldRow label="Objetivo del Proyecto" value={objective} />
            <FieldList label="Artefactos Aprobados" items={artifacts} emptyLabel="Sin artefactos previos" />
            <FieldList label="Archivos Disponibles" items={files} emptyLabel="Sin archivos generados" />
            <FieldList label="Restricciones de Trabajo" items={constraints} />
            <FieldList label="Tareas Pendientes" items={pendingTasks} />
            <FieldList label="Errores Conocidos" items={knownErrors} emptyLabel="Sin errores reportados" />
            <FieldRow label="Resultado Esperado" value={nextExpectedResult} />

            {reviewed && (
              <div className="flex items-start gap-2 p-3 bg-[#0A0C10] border border-[#10B981]">
                <CheckCircle size={16} className="text-[#10B981] mt-0.5" weight="fill" />
                <p className="text-sm terminal-font text-[#10B981]">
                  {mode === 'replacement' ? 'Paquete revisado. Listo para confirmar el reemplazo oficial.' : 'Paquete revisado. Listo para aprobar handoff.'}
                </p>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="p-6 pt-4 border-t border-[#1F2937] flex-row justify-end gap-2 sm:space-x-0">
          <Button
            data-testid="handoff-cancel-btn"
            onClick={handleCloseOrCancel}
            variant="outline"
            className="bg-transparent border border-[#1F2937] text-[#9CA3AF] hover:bg-[#1C1C24] hover:text-[#F3F4F6] terminal-font text-xs"
          >
            {mode === 'replacement' ? 'Cancelar (Mantener IA Actual)' : 'Reject / Cancel'}
          </Button>
          <Button
            data-testid="handoff-review-btn"
            onClick={() => setReviewed(true)}
            className="bg-[#13131A] border border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 terminal-font text-xs"
          >
            Revisar Paquete
          </Button>
          <Button
            data-testid="handoff-confirm-btn"
            onClick={handleConfirm}
            disabled={confirming}
            className="bg-[#D97706] text-[#0A0C10] hover:bg-[#D97706]/90 terminal-font text-xs font-bold"
          >
            {confirming ? (mode === 'replacement' ? 'Reemplazando...' : 'Aprobando...') : (mode === 'replacement' ? 'Confirm Replacement' : 'Approve Handoff')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HandoffPackageModal;
